// Deterministic rights engine. Pure functions only — no React, no I/O. Every
// metric is reproducible and explainable: it returns its value plus the formula,
// the inputs it used, the assumptions it made, and which inputs were missing.
// The UI renders these; it never computes rights itself. (Spec §7.)
import { classTotals } from '../state/matrix';

export const CALC_METHODS = [
  { id: 'total', label: 'שטח כולל (אחוזי בנייה = כלל השטח)' },
  { id: 'split', label: 'עיקרי/שירות בנפרד (אחוזים = עיקרי בלבד)' },
  { id: 'coverage_per_floor', label: 'תכסית לקומה (תכניות ישנות: אחוז × קומות)' },
];

const round = (n, d = 0) => {
  const f = 10 ** d;
  return Math.round(n * f) / f;
};
export const fmt = (n, d = 0) =>
  n == null || !Number.isFinite(n) ? '—' : round(n, d).toLocaleString('he-IL');

const HE = {
  parcelArea: 'שטח מגרש',
  farPct: 'אחוזי בנייה',
  coveragePct: 'תכסית מותרת %',
  densityPerDunam: 'צפיפות (יח"ד/דונם)',
  existingArea: 'שטח בנוי קיים',
  existingUnits: 'יח"ד קיימות',
  maxHeight: 'גובה מותר',
  maxStories: 'קומות מותר',
  parkingRatio: 'חניות ליח"ד',
};

export function inputLabel(key) {
  return HE[key] || key;
}

// Pull every engine input from the live project state, as plain numbers.
export function gatherInputs(project) {
  // Empty fields must read as null, not 0 — otherwise missing inputs compute as
  // zero (e.g. "remaining = allowed − 0") instead of surfacing as tasks.
  // (Number(null) === 0, so an explicit empty check is required.)
  const num = (f) => {
    if (!f || f.value == null || f.value === '') return null;
    const n = Number(f.value);
    return Number.isFinite(n) ? n : null;
  };
  const r = project.rights || {};
  const ct = classTotals(project.existingState.matrix);
  const matrixArea = ct.main + ct.service;
  // Prefer the classified matrix; fall back to the as-built summary total.
  const existingArea = matrixArea > 0 ? matrixArea : num(project.existingState.summary.builtArea);

  return {
    parcelArea: num(project.property.parcelArea),
    farPct: num(r.farPct),
    coveragePct: num(r.coveragePct),
    maxStories: num(r.maxStories),
    maxHeight: num(r.maxHeight),
    densityPerDunam: num(r.densityPerDunam),
    parkingRatio: num(r.parkingRatio),
    calcMethod: r.calcMethod?.value || 'total',
    existingMain: ct.main,
    existingService: ct.service,
    existingArea,
    existingAreaFromMatrix: matrixArea > 0,
    existingUnits: num(project.existingState.summary.units),
  };
}

// One metric. `deps` are the named numeric inputs it needs; any null dep makes
// the metric "missing" (a task), with value/formula suppressed.
function metric({ key, label, unit, deps, fn, formula, assumptions = [], decimals = 0 }) {
  const missing = Object.keys(deps).filter((k) => deps[k] == null);
  if (missing.length) {
    return { key, label, unit, decimals, value: null, formula: null, assumptions, inputs: deps, missingInputs: missing, status: 'missing' };
  }
  const value = fn(deps);
  return { key, label, unit, decimals, value, formula: formula(deps, value), assumptions, inputs: deps, missingInputs: [], status: 'ok' };
}

function calcMethodNote(method) {
  if (method === 'split') return 'אחוזי הבנייה מבוטאים כשטח עיקרי בלבד; שטחי שירות מעבר לכך.';
  if (method === 'coverage_per_floor') return 'שיטת תכניות ישנות: האחוז מבטא תכסית לקומה; השטח הכולל = אחוז × שטח מגרש × מספר קומות.';
  return 'אחוזי הבנייה מבוטאים כשטח כולל (עיקרי + שירות).';
}

// Compute the full metric set. Returns a flat list the UI groups/renders.
export function computeRights(inp) {
  const out = [];
  const perFloor = inp.calcMethod === 'coverage_per_floor';

  out.push(metric({
    key: 'farRatio', label: 'רח"ק (יחס שטח כולל)', unit: '', decimals: 2,
    deps: perFloor ? { farPct: inp.farPct, maxStories: inp.maxStories } : { farPct: inp.farPct },
    fn: (d) => perFloor ? (d.farPct / 100) * d.maxStories : d.farPct / 100,
    formula: (d, v) => perFloor
      ? `(${d.farPct}% ÷ 100) × ${d.maxStories} קומות = ${round(v, 2)}`
      : `${d.farPct}% ÷ 100 = ${round(v, 2)}`,
    assumptions: [calcMethodNote(inp.calcMethod)],
  }));

  out.push(metric({
    key: 'allowedTotal', label: 'שטח כולל מותר', unit: 'מ"ר',
    deps: perFloor
      ? { farPct: inp.farPct, parcelArea: inp.parcelArea, maxStories: inp.maxStories }
      : { farPct: inp.farPct, parcelArea: inp.parcelArea },
    fn: (d) => perFloor
      ? (d.farPct / 100) * d.parcelArea * d.maxStories
      : (d.farPct / 100) * d.parcelArea,
    formula: (d, v) => perFloor
      ? `(${d.farPct}% ÷ 100) × ${fmt(d.parcelArea)} × ${d.maxStories} קומות = ${fmt(v)} מ"ר`
      : `(${d.farPct}% ÷ 100) × ${fmt(d.parcelArea)} = ${fmt(v)} מ"ר`,
    assumptions: [calcMethodNote(inp.calcMethod)],
  }));

  // In per-floor mode the building percentage IS the coverage, so it stands in
  // when an explicit coverage percent wasn't provided.
  const covPct = inp.coveragePct ?? (perFloor ? inp.farPct : null);
  out.push(metric({
    key: 'allowedCoverage', label: 'תכסית מותרת', unit: 'מ"ר',
    deps: { coveragePct: covPct, parcelArea: inp.parcelArea },
    fn: (d) => (d.coveragePct / 100) * d.parcelArea,
    formula: (d, v) => `(${d.coveragePct}% ÷ 100) × ${fmt(d.parcelArea)} = ${fmt(v)} מ"ר`,
    assumptions: inp.coveragePct == null && perFloor
      ? ['בשיטת תכסית לקומה אחוז הבנייה משמש גם כתכסית.']
      : [],
  }));

  out.push(metric({
    key: 'allowedUnits', label: 'יח"ד מותר (לפי צפיפות)', unit: 'יח"ד',
    deps: { densityPerDunam: inp.densityPerDunam, parcelArea: inp.parcelArea },
    fn: (d) => Math.floor((d.densityPerDunam * d.parcelArea) / 1000),
    formula: (d, v) => `${d.densityPerDunam} × (${fmt(d.parcelArea)} ÷ 1000 דונם) = ${v} יח"ד`,
    assumptions: ['עיגול כלפי מטה ליחידה שלמה.'],
  }));

  out.push(metric({
    key: 'existingArea', label: 'שטח בנוי קיים', unit: 'מ"ר',
    deps: { existingArea: inp.existingArea },
    fn: (d) => d.existingArea,
    formula: (d, v) => `${fmt(v)} מ"ר`,
    assumptions: [inp.existingAreaFromMatrix
      ? 'מתוך מטריצת מצב קיים (עיקרי + שירות).'
      : 'מתוך סיכום מצב בנוי (תיק בניין/היתרים) — טעון אימות.'],
  }));

  out.push(metric({
    key: 'remainingArea', label: 'יתרת זכויות מול תכנית הבסיס (שטח)', unit: 'מ"ר',
    deps: perFloor
      ? { farPct: inp.farPct, parcelArea: inp.parcelArea, maxStories: inp.maxStories, existingArea: inp.existingArea }
      : { farPct: inp.farPct, parcelArea: inp.parcelArea, existingArea: inp.existingArea },
    fn: (d) => (perFloor
      ? (d.farPct / 100) * d.parcelArea * d.maxStories
      : (d.farPct / 100) * d.parcelArea) - d.existingArea,
    formula: (d, v) => {
      const allowed = perFloor ? (d.farPct / 100) * d.parcelArea * d.maxStories : (d.farPct / 100) * d.parcelArea;
      return `${fmt(allowed)} − ${fmt(d.existingArea)} = ${fmt(v)} מ"ר`;
    },
    assumptions: [calcMethodNote(inp.calcMethod)],
  }));

  out.push(metric({
    key: 'remainingUnits', label: 'יתרת יח"ד מול תכנית הבסיס', unit: 'יח"ד',
    deps: { densityPerDunam: inp.densityPerDunam, parcelArea: inp.parcelArea, existingUnits: inp.existingUnits },
    fn: (d) => Math.floor((d.densityPerDunam * d.parcelArea) / 1000) - d.existingUnits,
    formula: (d, v) => `${Math.floor((d.densityPerDunam * d.parcelArea) / 1000)} − ${d.existingUnits} = ${v} יח"ד`,
  }));

  out.push(metric({
    key: 'maxHeight', label: 'גובה מותר', unit: 'מ\'',
    deps: { maxHeight: inp.maxHeight },
    fn: (d) => d.maxHeight,
    formula: (d, v) => `לפי תכנית: ${v} מ'`,
  }));

  out.push(metric({
    key: 'maxStories', label: 'קומות מותר', unit: 'קומות',
    deps: { maxStories: inp.maxStories },
    fn: (d) => d.maxStories,
    formula: (d, v) => `לפי תכנית: ${v} קומות`,
  }));

  out.push(metric({
    key: 'parkingRequired', label: 'חניות נדרשות (הערכה)', unit: 'חניות',
    deps: { parkingRatio: inp.parkingRatio, densityPerDunam: inp.densityPerDunam, parcelArea: inp.parcelArea },
    fn: (d) => Math.ceil(d.parkingRatio * Math.floor((d.densityPerDunam * d.parcelArea) / 1000)),
    formula: (d, v) => `${d.parkingRatio} × ${Math.floor((d.densityPerDunam * d.parcelArea) / 1000)} יח"ד = ${v} חניות`,
    assumptions: ['הערכה לפי תקן חניה ידני; יש לאמת מול הנחיות מרחביות.'],
  }));

  // A negative remainder is a finding, not just a number: the existing
  // building exceeds the base plan's rights, usually because later layers
  // (e.g. Tama 38) granted more. Say so where the user will read it.
  for (const key of ['remainingArea', 'remainingUnits']) {
    const m = out.find((x) => x.key === key);
    if (m && m.status === 'ok' && m.value < 0) {
      m.assumptions = [...m.assumptions,
        'המבנה הקיים עולה על זכויות תכנית הבסיס — ככל הנראה מומשו זכויות מתכניות מאוחרות (למשל תמ"א 38). יש לבחון את שכבות ה-Plan Stack.'];
    }
  }

  return out;
}

// Convenience: the headline numbers a developer reads first.
export function headlineMetrics(metrics) {
  const by = (k) => metrics.find((m) => m.key === k);
  return [by('remainingArea'), by('remainingUnits'), by('allowedTotal'), by('allowedUnits')].filter(Boolean);
}
